

























































































import {
  computed,
  defineComponent,
  nextTick,
  onBeforeUnmount,
  ref,
  toRef,
  watch,
  watchEffect,
} from "@vue/composition-api";
import SwiperClass, { SwiperOptions } from "swiper";
import { Swiper, SwiperSlide } from "vue-awesome-swiper";
import "swiper/css/swiper.css";
import { lockBodyScroll } from "@scrile/tools/dist/lib/Scrollhelpers";
import { IS_BROWSER, isMobile } from "@/lib/browserUtils";
import BaseIcon from "@/components/Base/BaseIcon";

export default defineComponent({
  name: "FullScreen",
  components: { BaseIcon, Swiper, SwiperSlide },
  props: {
    zIndex: { type: Number },
    listLength: { type: Number, default: 1 },
    items: { type: Array, required: true },
    keyName: { type: String, required: true },
    scalable: { type: Boolean, default: true },
    showItems: { type: Boolean, default: true },
    portal: { type: Boolean, default: true },
  },
  setup(props, { emit }) {
    const zoomer = ref();
    const swiperOptions = ref<SwiperOptions>({
      slidesPerView: 1,
      allowTouchMove: isMobile(),
    });
    const isFullScreen = ref(false);
    const currentItem = ref(0);
    const currentScale = ref(1);
    const swiperComponentRef = ref<any>(null);
    const swiperComponentInstance = computed(() => swiperComponentRef.value?.$swiper as SwiperClass);
    const canGoBack = computed(() => currentItem.value > 0);
    const canGoNext = computed(() => currentItem.value < props.listLength - 1);
    const locked = ref(false);

    const setLocked = (value: boolean) => (locked.value = value);

    const moveNext = () => {
      swiperComponentInstance.value.slideNext();
      emit("moveNext");
    };
    const moveBack = () => {
      swiperComponentInstance.value.slidePrev();
      emit("moveBack");
    };

    const onSlideChange = () => {
      currentItem.value = swiperComponentInstance.value.activeIndex;
    };

    const openFullScreen = () => {
      if (isFullScreen.value) return;
      isFullScreen.value = true;
    };

    const closeFullScreen = () => {
      isFullScreen.value = false;
      emit("close");
    };

    const resetZoomer = () => {
      if (zoomer.value.scale <= 1 || !props.scalable) {
        zoomer.value.reset();
      }
    };

    const onZoomOutClick = () => {
      zoomer.value.zoomOut();
      resetZoomer();
    };

    watch(
      currentItem,
      (newVal: number) => {
        if (zoomer.value) {
          zoomer.value.reset();
          emit("changeIndex", newVal);
        }
      },
      { immediate: true }
    );

    watch(isFullScreen, (mode: boolean) => {
      nextTick().then(() => {
        swiperComponentInstance.value && swiperComponentInstance.value.slideTo(currentItem.value, 0);
      });
      lockBodyScroll(mode);
      emit("changeFullScreen", mode);
    });

    emit("setCurrentItem", (count: number) => (currentItem.value = Math.max(0, Math.min(props.listLength, count))));

    const onKeyDown = (event: KeyboardEvent) => {
      if (props.locked) return;
      if (event.key === "Escape") {
        closeFullScreen();
      } else if (event.key === "ArrowLeft") {
        moveBack && moveBack();
      } else if (event.key === "ArrowRight") {
        moveNext && moveNext();
      }
    };

    watchEffect(() => {
      if (isFullScreen.value) {
        IS_BROWSER && window.addEventListener("keydown", onKeyDown);
      } else {
        IS_BROWSER && window.removeEventListener("keydown", onKeyDown);
      }
    });

    onBeforeUnmount(() => {
      IS_BROWSER && window.removeEventListener("keydown", onKeyDown);
      if (isFullScreen.value) lockBodyScroll(false);
    });

    watch(toRef(props, "items"), (newItems: any[]) => {
      if (currentItem.value > newItems.length - 1) {
        currentItem.value = newItems.length - 1;
      }
    });

    const onTouchEvent = (ev: TouchEvent) => {
      if (ev.touches.length > 1) {
        ev.stopImmediatePropagation();
      }
    };

    return {
      isFullScreen,
      currentItem,
      canGoBack,
      canGoNext,
      locked,
      moveNext,
      moveBack,
      openFullScreen,
      closeFullScreen,
      onTouchEvent,
      zoomer,
      onZoomOutClick,
      resetZoomer,
      swiperOptions,
      swiperComponentInstance,
      swiperComponentRef,
      onSlideChange,
      currentScale,
      setLocked,
    };
  },
});
